// @ts-check
'use strict';
//***********************************************************************************
//***********************************************************************************
//******     CN-Map    **************************************************************
//******     Copyright(C) 2019-2020 EnerBIM                        ******************
//***********************************************************************************
//***********************************************************************************

//***********************************************************************************
//***********************************************************************************
//**** Fence type
//***********************************************************************************
//***********************************************************************************

import { cn_element_type } from './cn_element_type';

import { fh_add, fh_clone, fh_cross, fh_extruded_polygon, fh_mul, fh_normalize, fh_polygon, fh_sub } from '@acenv/fh-3d-viewer';
import { cn_element_type_visitor, cn_space_segment } from '..';
import { cn_middle, cnx_clone } from '../utils/cn_utilities';
import { cn_image_dir } from '../utils/image_dir.js';
import { cn_configuration, cn_configuration_choice, cn_configuration_line, cn_configuration_param, cn_configuration_tab } from './cn_configuration';
import { cn_topography } from './cn_topography';

var CN_BT_MIDDLE = 0;
var CN_BT_OUTER = 1;
var CN_BT_INNER = 2;

var CN_FT_CATEGORY_LIST = ['limit', 'fence', 'hedge'];
var CN_FT_LABEL_CATEGORY_LIST = ['Limite', 'Clôture', 'Haie'];

export class cn_fence_type extends cn_element_type {
    constructor(parent) {
        super(parent);
        this.class_name = 'cn_fence_type';
        this.name = '';
        this.wall_height = 1;
        this.fence_height = 1;
        this.thickness = 0.1;
        this.category = 'fence';
        this.free = false;
        this.step_length = 2;
    }

    //***********************************************************************************
    /**
     * Clone
     * @returns {cn_element_type}
     */
    clone() {
        var c = new cn_fence_type(this.parent);
        c.name = this.name;
        c.wall_height = this.wall_height;
        c.fence_height = this.fence_height;
        c.thickness = this.thickness;
        c.category = this.category;
        c.step_length = this.step_length;
        return c;
    }

    //***********************************************************************************
    get_generic_label() {
        return 'Type de clôture';
    }

    //***********************************************************************************
    //**** keys
    //***********************************************************************************
    model_keys() {
        return ['name', 'wall_height', 'fence_height', 'step_length', 'thickness', 'category'];
    }

    //***********************************************************************************
    //**** set category
    //***********************************************************************************
    set_category(category) {
        if (this.category == category) return;
        this.category = category;

        if (category == 'fence') {
            this.wall_height = 1;
            this.fence_height = 1;
            this.thickness = 0.2;
            this.free = false;
        } else if (category == 'hedge') {
            this.wall_height = 2;
            this.fence_height = 0;
            this.thickness = 0.4;
            this.free = false;
        } else if (category == 'limit') {
            this.wall_height = 0;
            this.fence_height = 0;
            this.thickness = 0;
            this.free = true;
        } else this.set_category('fence');
    }

    //***********************************************************************************
    //**** default
    //***********************************************************************************
    /**
     * Returns a default list of fence types
     * @returns {Array<cn_fence_type>} cn_element_type
     */
    static default_types(parent) {
        var ft_list = [];
        var ft = null;

        ft = new cn_fence_type(parent);
        ft.set_category('limit');
        ft_list.push(ft);

        ft = new cn_fence_type(parent);
        ft.set_category('fence');
        ft_list.push(ft);

        ft = new cn_fence_type(parent);
        ft.set_category('fence');
        ft.fence_height = 0;
        ft_list.push(ft);

        ft = new cn_fence_type(parent);
        ft.set_category('hedge');
        ft_list.push(ft);

        return ft_list;
    }

    //***********************************************************************************
    //**** serialize
    //***********************************************************************************
    serialize() {
        var json = this.serialize_element();
        json.class_name = 'cn_fence_type';
        json.name = this.name;
        json.wall_height = this.wall_height;
        json.fence_height = this.fence_height;
        json.thickness = this.thickness;
        json.category = this.category;
        json.step_length = this.step_length;
        return json;
    }

    static unserialize(json, parent) {
        var wt = new cn_fence_type(parent);
        wt.unserialize_element(json);
        wt.set_category(json.category);
        if (typeof (json.name) == 'string')
            wt.name = json.name;
        if (typeof (json.wall_height) == 'number')
            wt.wall_height = json.wall_height;
        if (typeof (json.fence_height) == 'number')
            wt.fence_height = json.fence_height;
        if (typeof (json.thickness) == 'number')
            wt.thickness = json.thickness;
        if (typeof (json.step_length) == 'number')
            wt.step_length = json.step_length;
        return wt;
    }

    //***********************************************************************************
    //**** Build label
    //***********************************************************************************
    get_label() {
        if (this.name != '') return this.name;
        var element_type_name = '';

        if (this.category == 'limit')
            return 'Limite';
        if (this.category == 'fence') {
            if (this.wall_height > 0 && this.fence_height > 0) {
                return 'Clôture maçonnée ' + (this.wall_height * 100).toFixed(0) + 'cm + ' + (this.fence_height * 100).toFixed(0) + 'cm';
            }
            if (this.wall_height > 0)
                return 'Mur ' + (this.wall_height * 100).toFixed(0) + 'cm';
            return 'Clôture ' + (this.fence_height * 100).toFixed(0) + 'cm';
        }

        return 'Haie ' + (this.wall_height * 100).toFixed(0) + 'cm';
    }

    //***********************************************************************************
    /**
     * category label
     * @returns {string} displayable category
     */
    get_category_label() {
        if (this.category == 'limit')
            return 'Limite';
        if (this.category == 'fence') {
            if (this.wall_height > 0 && this.fence_height > 0) {
                return 'Clôture maçonnée';
            }
            if (this.wall_height > 0)
                return 'Mur';
            return 'Clôture';
        }

        return 'Haie';
    }

    //***********************************************************************************
    //**** Returns description
    //***********************************************************************************

    /**
     * Returns an array of objects describing the type.
     * @returns {{label: string, value?: any, decimals?: number, unit?: string}[]}
     */
    get_description() {
        var description = [];

        var cat = { label: 'Catégorie' };
        var index = CN_FT_CATEGORY_LIST.indexOf(this.category);
        cat.value = CN_FT_LABEL_CATEGORY_LIST[index];
        description.push(cat);

        var height = { label: 'Hauteur' };
        description.push(height);
        height.value = (this.wall_height + this.fence_height) * 100;
        height.decimals = 0;
        height.unit = 'cm';

        if (this.wall_height > 0) {
            var th = { label: 'Epaisseur' };
            description.push(th);
            th.value = this.thickness * 100;
            th.decimals = 0;
            th.unit = 'cm';
        }

        return description;
    }

    //***********************************************************************************
    /**
     * draw svg icon
     * @param {number} width
     * @param {number} height
     * @returns {string}
     */
    draw_svg_icon(width, height) {
        var html = '';
        if (this.category == 'limit') {
            html += '<line x1=\'' + (width / 2) + '\' y1=\'0\' x2=\'' + (width / 2) + '\' y2=\'' + height + '\' style=\'stroke:black; stroke-width: 2px; stroke-dasharray: 5,5;\' />';
            return html;
        }

        var zscale = (this.wall_height + this.fence_height > 2.5) ? 1 / (this.wall_height + this.fence_height) : 1 / 2.5;
        zscale *= height;

        function to_screen(y) {
            return height - y * zscale
        };

        if (this.category == 'hedge') {
            html += `<image xlink:href="${cn_image_dir()}texture_hedge.jpg" x="0" y="${to_screen(this.wall_height)}" width="${width}" height="${zscale * this.wall_height}" preserveAspectRatio="xMidYMid slice" />`;
            return html;
        }

        if (this.category == 'fence') {
            if (this.wall_height > 0) {
                html += `<rect x="0" y="${to_screen(this.wall_height)}" width="${width}" height="${zscale * this.wall_height}" style="fill:rgb(200,200,200);stroke:black;" />`;
            }

            if (this.fence_height > 0) {
                var rail_size = height * 0.02;
                var rail_height = rail_size * 2;
                var nb_rails = 8;
                var rail_spacing = (width - rail_size) / (nb_rails - 1);

                html += `<rect x="0" y="${to_screen(this.wall_height + this.fence_height)}" width="${width}" height="${rail_height}" style="fill:rgb(50,100,50);stroke:black;" />`;
                var x = 0;
                for (var n = 0; n < nb_rails; n++) {
                    html += `<rect x="${x}" y="${to_screen(this.wall_height + this.fence_height - rail_height / zscale)}" width="${rail_size}" height="${this.fence_height * zscale - rail_height}" style="fill:rgb(50,100,50);stroke:black;" />`;
                    x += rail_spacing;
                }
            }
        }
        return html;
    }

    //***********************************************************************************
    //**** Slicing
    //***********************************************************************************
    compute_wall_slices(wall_interface) {
        if (this.category == 'limit') return [];

        var valid_openings = wall_interface.wall.openings.filter(op => op.valid);
        if (this.category == 'hedge') max_length = 2;
        const wall = wall_interface.wall;

        let wall_parts = [wall_interface.shape.map(sh => wall.compute_abcissa(sh))];

        //*** Cut with openings */
        valid_openings.forEach(op => {
            const x0 = op.position;
            const x1 = x0 + op.get_width();
            const new_wall_parts = [];
            wall_parts.forEach(wp => {
                const xx0 = Math.min(wp[0], wp[1]);
                const xx1 = Math.max(wp[2], wp[3]);
                if (x1 > xx0 && x0 < xx1) {
                    if (x0 > xx0)
                        new_wall_parts.push([wp[0], wp[1], x0, x0]);
                    if (x1 < xx1)
                        new_wall_parts.push([x1, x1, wp[2], wp[3]]);
                }
                else
                    new_wall_parts.push(wp);
            });
            wall_parts = new_wall_parts;
        });

        //*** Cut with length */
        const new_wall_parts = [];
        var max_length = (this.step_length < 1) ? 10000 : this.step_length;
        wall_parts.forEach(wp => {
            const xx0 = Math.min(wp[0], wp[1]);
            const xx1 = Math.max(wp[2], wp[3]);
            if (xx1 - xx0 > max_length) {
                const nb_parts = Math.ceil(xx1 - xx0 / max_length);
                const part_size = (xx1 - xx0) / nb_parts;
                for (var k = 0; k < nb_parts; k++) {
                    const nwp = [];
                    if (k == 0) nwp.push(wp[0], wp[1]);
                    else nwp.push(xx0 + part_size * k, xx0 + part_size * k);
                    if (k == nb_parts - 1) nwp.push(wp[2], wp[3]);
                    else nwp.push(xx0 + part_size * (k + 1), xx0 + part_size * (k + 1));
                    new_wall_parts.push(nwp);
                }
            }
            else
                new_wall_parts.push(wp);
        });
        return new_wall_parts;
    }

    /**
     * Compute lines of a space segment
     * @param {cn_space_segment} space_segment
     * @param {cn_topography} topography
     * @returns
     */
    compute_space_line_slices(space_segment, topography = null) {
        if (!space_segment.wall) return [];
        if (space_segment.wall_side >= 2) return [];
        const wall = space_segment.wall;
        const wall_interfaces = space_segment.wall.wall_interfaces.filter(wi => wi.space_segments.includes(space_segment));
        if (wall_interfaces.length == 0) return [];

        let wall_parts = [];
        wall_interfaces.forEach(wi => wall_parts = wall_parts.concat(this.compute_wall_slices(wi)))

        const space_lines = [];
        const s0 = space_segment.wall_positions[space_segment.wall_side];
        const s1 = space_segment.wall_positions[1 - space_segment.wall_side];

        wall_parts.forEach(wp => {
            const wp0 = (space_segment.wall_side == 0) ? wp[0] : wp[1];
            const wp1 = (space_segment.wall_side == 0) ? wp[3] : wp[2];
            const x0 = Math.max(wp0, s0);
            const x1 = Math.min(wp1, s1);
            if (x1 > x0) {
                let zmin = 0;
                if (topography) {
                    zmin = 10000;
                    wp.forEach((x, i) => {
                        const p = wall.compute_side_position_from_abcissa(x, (i == 0 || i == 3) ? 0 : 1);
                        var z = topography.compute_height(p);
                        if (z < zmin) zmin = z;
                    });
                    zmin -= topography.z;
                }

                if (space_segment.wall_side == 0) {
                    space_lines.push(cnx_clone(wall.compute_side_position_from_abcissa(x0, 0), zmin));
                    space_lines.push(cnx_clone(wall.compute_side_position_from_abcissa(x1, 0), zmin));
                }
                else {
                    space_lines.push(cnx_clone(wall.compute_side_position_from_abcissa(x1, 1), zmin));
                    space_lines.push(cnx_clone(wall.compute_side_position_from_abcissa(x0, 1), zmin));
                }
            }
        });

        return space_lines;
    }

    //***********************************************************************************
    //**** 3D geometry
    //***********************************************************************************
    build_extruded_polygons(z, wall_interface) {
        var extruded_polygons = [];
        if (this.category == 'limit') return extruded_polygons;

        var wall_parts = this.compute_wall_slices(wall_interface);
        const wall = wall_interface.wall;

        for (var nop = 0; nop < wall_parts.length; nop++) extruded_polygons.push([]);

        //*** Wall balcony
        if (this.wall_height > 0 && this.thickness > 0) {
            wall_parts.forEach((wp, nop) => {
                var ctr2d = [wall.compute_side_position_from_abcissa(wp[0], 0),
                wall.compute_side_position_from_abcissa(wp[1], 1),
                wall.compute_side_position_from_abcissa(wp[2], 1),
                wall.compute_side_position_from_abcissa(wp[3], 0)];
                var ctr3d = [];
                for (var i in ctr2d) {
                    var v = ctr2d[i];
                    ctr3d.push([v[0], v[1], z]);
                }
                var epg = new fh_extruded_polygon();
                extruded_polygons[nop].push(epg);
                epg.polygon = new fh_polygon(ctr3d[0], [0, 0, 1]);
                epg.polygon.add_contour(ctr3d);
                epg.direction = [0, 0, this.wall_height];

                if (this.category == 'fence')
                    epg.color = [0.8, 0.8, 0.8, 1];
                else {
                    epg.color = [0.2, 0.5, 0.3, 1];
                    epg.texture = cn_image_dir() + 'texture_hedge.jpg';
                }
            });
        }
        //*** railing balcony
        if (this.fence_height > 0) {
            var color = [0.8, 1, 0.8, 1];
            var ramp_thickness = 0.03;
            var bottom_height = 0.1;
            var rail_thickness = 0.015;
            var start_rail_thickness = 0.03;

            wall_parts.forEach((wp, nop) => {
                var v0 = cn_middle(wall.compute_side_position_from_abcissa(wp[0], 0), wall.compute_side_position_from_abcissa(wp[1], 1));
                v0.push(z + this.wall_height);

                var v1 = cn_middle(wall.compute_side_position_from_abcissa(wp[2], 1), wall.compute_side_position_from_abcissa(wp[3], 0));
                v1.push(z + this.wall_height);

                var direction = fh_sub(v1, v0);
                var length = fh_normalize(direction);
                var dz = [0, 0, 1];
                var dx = fh_cross(direction, dz);

                var p0 = fh_clone(v0);

                //*** ramp
                p0[2] = z + this.wall_height + this.fence_height - ramp_thickness * 0.5;
                var epg = new fh_extruded_polygon();
                extruded_polygons[nop].push(epg);
                epg.tube(p0, fh_mul(direction, length), dx, ramp_thickness * 2, ramp_thickness);
                epg.color = color;

                //*** start rails
                epg = new fh_extruded_polygon();
                extruded_polygons[nop].push(epg);
                epg.tube(v0, fh_mul(dz, this.fence_height), dx, start_rail_thickness, start_rail_thickness);
                epg.color = color;

                epg = new fh_extruded_polygon();
                extruded_polygons[nop].push(epg);
                epg.tube(v1, fh_mul(dz, this.fence_height), dx, start_rail_thickness, start_rail_thickness);
                epg.color = color;

                //*** rails
                var nb_rails = 1 + Math.floor(length / (0.11 - rail_thickness));
                var rail_spacing = length / nb_rails;
                var ez = fh_mul(dz, this.fence_height - ramp_thickness);
                for (var n = 1; n < nb_rails; n++) {
                    epg = new fh_extruded_polygon();
                    extruded_polygons[nop].push(epg);
                    epg.tube(fh_add(v0, fh_mul(direction, rail_spacing * n)), ez, dx, rail_thickness, rail_thickness);
                    epg.color = color;
                }
            });
        }
        return extruded_polygons;
    }

    //***********************************************************************************
    //**** Get configuration
    //***********************************************************************************
    static configuration() {
        var configuration = new cn_configuration();

        //***********************************************
        //*** Railing configuration
        var railing = new cn_configuration_tab('Limite', 'limit');
        configuration.add_tab(railing);

        var railing_list = new cn_configuration_line('Limite');
        railing.add_line(railing_list);

        railing_list.add_choice(new cn_configuration_choice());

        //***********************************************
        //*** fence configuration
        var wall = new cn_configuration_tab('Clôture', 'fence');
        configuration.add_tab(wall);

        var wall_list = new cn_configuration_line('Clôture');
        wall.add_line(wall_list);

        wall_list.add_param(new cn_configuration_param('Epaisseur', 'thickness', 0, 100, 'cm'));
        wall_list.add_param(new cn_configuration_param('Hauteur maçonnée', 'wall_height', 0, 200, 'cm'));
        wall_list.add_param(new cn_configuration_param('Hauteur de grille', 'fence_height', 0, 200, 'cm'));
        wall_list.add_param(new cn_configuration_param('Longueur des tronçons', 'step_length', 0, 100, 'm'));

        wall_list.add_choice(new cn_configuration_choice());

        //***********************************************
        //*** hedge configuration
        var hedge = new cn_configuration_tab('Haie', 'hedge');
        configuration.add_tab(hedge);

        var hedge_list = new cn_configuration_line('Haie');
        hedge.add_line(hedge_list);

        hedge_list.add_param(new cn_configuration_param('Epaisseur', 'thickness', 0, 100, 'cm'));
        hedge_list.add_param(new cn_configuration_param('Hauteur', 'wall_height', 0, 200, 'cm'));

        hedge_list.add_choice(new cn_configuration_choice());

        return configuration
    }

    //***********************************************************************************
    //**** Fill configuration
    //***********************************************************************************
    fill_configuration(config = null) {
        var configuration = (config) ? config : cn_fence_type.configuration();

        //*** default to railing
        configuration.selection = 0;

        //*** find proper tab
        var tab_index = configuration.get_tab_index(this.category);
        if (tab_index < 0) return false;
        configuration.selection = tab_index;

        if (this.category == 'fence') {
            configuration.tabs[1].lines[0].get_param('thickness').value = this.thickness * 100;
            configuration.tabs[1].lines[0].get_param('wall_height').value = this.wall_height * 100;
            configuration.tabs[1].lines[0].get_param('fence_height').value = this.fence_height * 100;
            configuration.tabs[1].lines[0].get_param('step_length').value = this.step_length;
        }

        if (this.category == 'hedge') {
            configuration.tabs[2].lines[0].get_param('thickness').value = this.thickness * 100;
            configuration.tabs[2].lines[0].get_param('wall_height').value = this.wall_height * 100;
        }

        return configuration;
    }

    //***********************************************************************************
    //**** Load configuration
    //***********************************************************************************
    load_configuration(configuration, actual_change = false) {
        var configuration_tab = configuration.tabs[configuration.selection];
        if (this.category != configuration_tab.name) {
            this.set_category(configuration_tab.name);
            return true;
        }

        var configuration_line = configuration_tab.lines[configuration_tab.selection];
        if (this.category == 'fence') {
            this.thickness = 0.01 * configuration_line.get_param('thickness').value;
            this.wall_height = 0.01 * configuration_line.get_param('wall_height').value;
            this.fence_height = 0.01 * configuration_line.get_param('fence_height').value;
            this.step_length = configuration_line.get_param('step_length').value;
        }
        if (this.category == 'hedge') {
            this.thickness = 0.01 * configuration_line.get_param('thickness').value;
            this.wall_height = 0.01 * configuration_line.get_param('wall_height').value;
        }
        return true;
    }

    /**
     * Accept element type visitor
     *
     * @param {cn_element_type_visitor} element_type_visitor
     */
    accept_visitor(element_type_visitor) {
        element_type_visitor.visit_fence_type(this);
    }

    /**
     * returns true if thickness hasn't changed since date
     * @param {number} date
     * @returns {boolean}
     */
    up_to_date_thickness(date) {
        return this.get_date('thickness') <= date;
    }
}

